import { post, get, clearSession, currentSession, saveSession, type Session, type SessionUser } from './client'

/**
 * Surface d'authentification. Le module `client` sait *stocker* une session ;
 * c'est ici qu'on sait comment on l'*obtient*. Cette séparation permet de
 * changer de fournisseur d'identité (OIDC, SSO) sans toucher au client HTTP.
 */

export interface LoginPayload {
  email: string
  password: string
}

export async function login(payload: LoginPayload): Promise<Session> {
  const session = await post<Session>('/auth/login', payload)
  saveSession(session)
  return session
}

/**
 * Déconnexion. Le jeton de rafraîchissement est révoqué côté serveur, mais la
 * session locale est effacée QUOI QU'IL ARRIVE : si l'appel échoue (hors ligne,
 * API redémarrée), l'utilisateur doit tout de même se retrouver déconnecté sur
 * ce poste. L'inverse — rester connecté parce que le réseau est tombé — serait
 * un défaut de sécurité.
 */
export async function logout(): Promise<void> {
  const refreshToken = currentSession()?.refreshToken
  try {
    if (refreshToken) await post<void>('/auth/logout', { refreshToken })
  } catch {
    // Silence volontaire : voir ci-dessus.
  } finally {
    clearSession()
  }
}

/** Récupère l'utilisateur courant depuis le serveur (vérifie aussi le jeton). */
export function me(): Promise<SessionUser> {
  return get<SessionUser>('/auth/me')
}

/**
 * Revient chez soi après un « jump » backoffice. Idempotent côté serveur : sans
 * jump en cours, la route rend simplement le profil courant.
 *
 * Ici et non dans `superuser.ts` : la route n'est PAS sous la garde du
 * périmètre plateforme — pendant le jump, l'organisation effective est celle du
 * client et cette garde refuserait l'agent. C'est un acte de SESSION.
 */
export function quitterJump(): Promise<SessionUser> {
  return post<SessionUser>('/auth/jump/return')
}

/**
 * Demande un lien de réinitialisation de mot de passe.
 *
 * Le serveur répond 202 QUELLE QUE SOIT l'existence du compte (anti-énumération)
 * : l'interface doit donc toujours afficher le même message de confirmation,
 * jamais « compte inconnu ».
 */
export function requestPasswordReset(email: string): Promise<{ message: string }> {
  return post<{ message: string }>('/auth/forgot-password', { email })
}

/** Réinitialise le mot de passe à partir du jeton reçu par courriel. */
export function resetPassword(corps: { token: string; password: string }): Promise<void> {
  return post<void>('/auth/reset-password', corps)
}

/**
 * Changement de mot de passe depuis une session ouverte.
 *
 * Le serveur révoque TOUTES les sessions du compte, y compris celle de
 * l'appelant : l'appelant doit donc déconnecter localement après succès, puis
 * faire re-saisir le nouveau mot de passe — double vérification gratuite contre
 * la faute de frappe au choix du mot de passe.
 */
export function changePassword(corps: {
  currentPassword: string
  newPassword: string
}): Promise<void> {
  return post<void>('/auth/change-password', corps)
}
